import {AceLoggerService} from '@logging/ace-logger.service';
import {Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot} from '@angular/router';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Client} from '@app/layout/admin/services/api/client-interfaces';
import {PagedModel} from '@shared/api/model/paged-model';
import {BaseUserService} from '@shared/services/api/base-user.service';
import {AceLogger} from '@logging/ace-logger';
import {UserProfileService} from "@shared/services/api/user-profile.service";
import {APIPageInfo, HttpUtilityService} from "@shared/services/api/http-utility-service";
import {PageInfo} from "@shared/components/ace-table/ace-table.component";
import {BaseIntervalRefreshedCache} from "@shared/caches/interval-refreshed-cache";
import {WebBrowserCommunicationService} from "@shared/services/web-browser/web-browser-communication.service";
import {AngularPageVisibilityService} from '../web-browser/page-visibility.service';
import {APIResponse} from '@app/layout/admin/services/api/api.interface';
import {StringUtils} from '@shared/util/general/string-utils';
import {APIClientEntity, ClientId} from "@shared/services/api/user-entity";


export interface ClientSearchParams {
  clientName: string;
}

interface ClientAPICreate {
  client_id: ClientId;
}

interface ClientAPIResponse {
  client: APIClientEntity;
}

interface ClientsAPIResponse {
  clients: APIClientEntity[];
}

enum API_OPERATIONS {
  CLIENT_ID = 'client_id',
  CLIENT = 'client',
  CLIENTS = 'clients',
}
export const clientResolver: ResolveFn<Client> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<Client> => {
  return inject(ClientService).getClientFromAPI(StringUtils.stringToNumber(route.paramMap.get('id')));
};

@Injectable({
  providedIn: 'root'
})
export class ClientService extends BaseUserService {

  private readonly logger: AceLogger;
  private readonly clientCache: ClientCache;
  private readonly FETCH_PAGE_SIZE = 10000;
  private headers: HttpHeaders = this.getHeaders();

  private static map_client_from_api(client: APIClientEntity): Client {
    return new Client(client.client_id, client.client_name, (client.logo_url ? new URL(client.logo_url) : null), client.allowed_actions);
  }

  private static map_client_to_api(client: Client) {
    return {
      client_name: client.name,
      ...(client.id !== null && { client_id: client.id })
    };
  }

  constructor(private http: HttpClient,
              private httpUtilityService: HttpUtilityService,
              pageVisibilityService: AngularPageVisibilityService,
              browserCommsService: WebBrowserCommunicationService,
              userProfileService: UserProfileService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(ClientService.name);
    this.clientCache = new ClientCache(pageVisibilityService, browserCommsService, userProfileService, logService);
    this.clientCache.init(this.fetchClients, 'id');
  }

  private readonly fetchClients: () => Observable<Client[]> = () => {
    return this.httpUtilityService.pageUntilComplete(
      (pageInfo: PageInfo) => this.getClientsFromAPI(pageInfo.first, pageInfo.rows),
      { first: 0, rows: this.FETCH_PAGE_SIZE });
  };

  public getClients(searchParams: ClientSearchParams, pageInfo: APIPageInfo, ignoreCache?: boolean): Observable<PagedModel<Client[]>> {
    return this.loadClients(searchParams,  pageInfo, ignoreCache);
  }

  public getAllClients(searchParams?: ClientSearchParams, ignoreCache?: boolean): Observable<Client[]> {
    return this.loadClients(searchParams, null, ignoreCache).pipe(map(pagedClients => pagedClients.data));
  }

  private loadClients(searchParams?: ClientSearchParams, pageInfo?: APIPageInfo, ignoreCache?: boolean): Observable<PagedModel<Client[]>> {
    return this.clientCache.getItemsById(null, ignoreCache)
      .pipe(map((clientsById: Map<ClientId, Client>) =>
        this.filterClientsByNameSearchText(clientsById, searchParams?.clientName ?? null, pageInfo)
      ));
  }

  private filterClientsByNameSearchText(clientsById: Map<ClientId, Client>, searchText: string | null, pageInfo: APIPageInfo | null): PagedModel<Client[]> {
    const searchTextLowerTrimmed = searchText?.trim().toLowerCase();
    const paginating = !!pageInfo;
    if (searchTextLowerTrimmed) {
      const result = [];
      let count = 0;
      for (const client of clientsById.values()) {
        const match = client.name.toLowerCase().includes(searchTextLowerTrimmed);
        if (match) {
          if (!paginating || (count >= pageInfo.first && result.length < pageInfo.rows))  {
            result.push(client);
          }
          count++;
        }
      }
      return {
        data: result,
        count
      };
    }
    const allClients = Array.from(clientsById.values());
    return {
      data: paginating ? allClients.slice(pageInfo.first, pageInfo.first + pageInfo.rows) : allClients,
      count: clientsById.size
    };
  }

  createClient(client: Client): Observable<ClientId> {
    const options = { headers: this.headers };
    return this.http.post(this.getGateway() + '/users/clients', ClientService.map_client_to_api(client), options).pipe(
      tap(() => this.clearClientCache()),
      map((response: APIResponse<ClientAPICreate>) => this.httpUtilityService.extractResponseData(response, API_OPERATIONS.CLIENT_ID)),
    );
  }

  updateClient(client: Client): Observable<Object> {
    const options = { headers: this.headers };
    return this.http.put(this.getGateway() + '/users/clients/' + client.id, ClientService.map_client_to_api(client), options)
      .pipe(tap(() => this.clearClientCache()));
  }

  updateClientLogo(clientId: ClientId, logo: { data: string, content_type: string }): Observable<Object> {
    const options = { headers: this.headers };
    return this.http.post(this.getGateway() + '/users/clients/' + clientId + '/logo', logo, options)
      .pipe(tap(() => this.clearClientCache()));
  }

  deleteClientLogo(clientId: ClientId): Observable<Object> {
    const options = { headers: this.headers };
    return this.http.delete(this.getGateway() + '/users/clients/' + clientId + '/logo', options)
      .pipe(tap(() => this.clearClientCache()));
  }

  private clearClientCache(): void {
    this.clientCache.clearCache();
  }

  private getClientsFromAPI(first: number, max: number): Observable<PagedModel<Client[]>> {
    let params = new HttpParams().set('first', first.toString()).set('max', max.toString());
    const options = { headers: this.headers, params: params };
    return this.http.get(this.getGateway() + '/users/clients', options)
      .pipe(map((response: APIResponse<ClientsAPIResponse>) => {
        const clientResult: PagedModel<APIClientEntity[]> = this.httpUtilityService.extractPagedResponse(response, API_OPERATIONS.CLIENTS);
        return {
          data: clientResult.data.map(ClientService.map_client_from_api),
          count: clientResult.count,
        };
      }));
  }

  getClientFromAPI(clientId: ClientId): Observable<Client> {
    const options = { headers: this.headers };
    return this.http.get(this.getGateway() + '/users/clients/' + clientId, options).pipe(
      map((response: APIResponse<ClientAPIResponse>) =>
        ClientService.map_client_from_api(this.httpUtilityService.extractResponseData(response, API_OPERATIONS.CLIENT))));
  }


}


class ClientCache extends BaseIntervalRefreshedCache<Client, 'id'> {
  // Need to keep a static array of ClientCache(s) as the Page Visibility events are not fired with reference to ClientCache
  // instance (i.e. this is undefined inside the Page Visibility event listeners). Therefore, need a way to get to the
  // ClientCache instance
  static LISTENERS_UGLY_HACK: ClientCache[] = [];

  constructor(pageVisibilityService: AngularPageVisibilityService,
              browserCommsService: WebBrowserCommunicationService,
              userProfileService: UserProfileService,
              logService: AceLoggerService) {
    super(pageVisibilityService, browserCommsService, userProfileService, logService.create(ClientCache.name), WebBrowserCommunicationService.KEYS.CLIENTS);
  }

  protected populateStaticCacheArrayHack(): void {
    ClientCache.LISTENERS_UGLY_HACK.push(this);
  }

  whenPageVisible(): void {
    ClientCache.onPageVisible(ClientCache.LISTENERS_UGLY_HACK);
  }
}
